import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToSection } from "@/lib/lenis";

const LINKS = [
  { name: "About", target: "#about", testid: "nav-link-about" },
  { name: "Sectors", target: "#sectors", testid: "nav-link-sectors" },
  { name: "Approach", target: "#approach", testid: "nav-link-approach" },
  { name: "Contact", target: "#contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    scrollToSection(target);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-500 border-b ${
        scrolled
          ? "backdrop-blur-md bg-white/90 border-[#D5E2EC] shadow-[0_2px_20px_rgba(18,52,78,0.06)]"
          : "bg-white border-[#E8F0F6]"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#hero")}
          className="flex items-center group"
        >
          <img
            src="/logo.png"
            alt="Jamsac Investments Limited"
            className="h-10 w-auto transition-opacity duration-300 group-hover:opacity-80"
          />
        </button>

        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((link) => (
            <button
              key={link.name}
              data-testid={link.testid}
              onClick={() => go(link.target)}
              className="text-sm font-medium text-[#3D5266] hover:text-[#1F6191] transition-colors duration-300 tracking-wide"
            >
              {link.name}
            </button>
          ))}
          <button
            data-testid="nav-cta-partner"
            onClick={() => go("#contact")}
            className="text-xs font-semibold uppercase tracking-[0.12em] px-6 py-3 rounded-full bg-[#1F6191] text-white hover:bg-[#12344E] transition-colors duration-300"
          >
            Partner With Us
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          className="md:hidden text-[#12344E]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-white border-t border-[#E8F0F6] px-6 py-6 flex flex-col gap-5">
          {LINKS.map((link) => (
            <button
              key={link.name}
              data-testid={`mobile-${link.testid}`}
              onClick={() => go(link.target)}
              className="text-left text-base font-medium text-[#12344E] hover:text-[#1F6191] transition-colors"
            >
              {link.name}
            </button>
          ))}
          <button
            data-testid="mobile-nav-cta-partner"
            onClick={() => go("#contact")}
            className="text-xs font-semibold uppercase tracking-[0.12em] px-6 py-3.5 rounded-full bg-[#1F6191] text-white text-center"
          >
            Partner With Us
          </button>
        </div>
      )}
    </motion.header>
  );
}
